import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { GlassCard } from '../../components/common/GlassCard';
import { toast } from 'sonner';
import { Building2, Mail, ArrowLeft, CheckCircle, Send } from 'lucide-react';

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
      toast.success('If an account exists, you will receive reset instructions.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4" data-testid="forgot-password-page">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1676287734314-b7616c3c5e5a?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 to-slate-950" />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <Building2 className="w-10 h-10 text-emerald-500" />
            <span className="text-2xl font-bold text-gradient-gold">Sam Rumah</span>
          </Link>
          <h1 className="text-2xl font-bold text-white">
            {submitted ? 'Check Your Email' : 'Forgot Password?'}
          </h1>
          <p className="text-slate-400 mt-1">
            {submitted 
              ? 'We\'ve sent you password reset instructions' 
              : 'Enter your email and we\'ll send you reset instructions'}
          </p>
        </div>

        <GlassCard>
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Email Sent!</h3>
              <p className="text-slate-400 text-sm mb-6">
                If an account exists for <span className="text-emerald-400">{email}</span>, 
                you'll receive an email with instructions to reset your password.
              </p>
              <p className="text-slate-500 text-xs mb-6">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                  }}
                  variant="outline"
                  className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  Try Different Email
                </Button>
                <Link to="/login">
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                    required
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600 btn-glow"
                disabled={loading}
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Reset Link
                  </>
                )}
              </Button>

              <div className="text-center pt-2">
                <Link 
                  to="/login" 
                  className="text-slate-400 text-sm hover:text-emerald-400 inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </GlassCard>
      </div>
    </div>
  );
};

export default ForgotPassword;
